import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { cn } from "@veil/ui/lib/utils";
import { Check, Loader2, Minus, X } from "lucide-react";

export type StepStatus = "pending" | "running" | "done" | "failed" | "skipped";

export interface TimelineStep {
	step: string;
	label: string;
	status: StepStatus;
	startedAt?: string;
	finishedAt?: string;
	error?: string;
	/**
	 * Live subtext rendered under the label while the step is running.
	 * Used today by the webroot lock to surface "Waiting for prior
	 * publish (op abcd1234)…" instead of the generic "In progress…".
	 */
	note?: string;
}

interface OperationStepTimelineProps {
	steps: TimelineStep[];
	/**
	 * Operation status from the parent. Used to decide whether the
	 * "currently running" pulse should keep going after the SSE has
	 * ended — once the op transitions to succeeded/failed we freeze the
	 * timeline so a trailing pending step doesn't keep spinning.
	 */
	operationStatus?: string;
}

/**
 * Vertical step timeline rendered above the live log on the Operation
 * detail page. Driven entirely by the structured progress array the
 * backend persists on `Operation.progress` (plus SSE `step` events for
 * live updates).
 *
 * Visual contract:
 *   pending  → muted circle, label only
 *   running  → blue spinner, label + "In progress…" italic subtext
 *   done     → green check, label + duration
 *   failed   → red X, label + `error` text in destructive color
 *   skipped  → gray dash, label dimmed
 */
export function OperationStepTimeline({
	steps,
	operationStatus,
}: OperationStepTimelineProps) {
	if (!steps || steps.length === 0) return null;
	const opTerminal =
		operationStatus !== undefined && operationStatus !== "running";

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Pipeline progress</CardTitle>
			</CardHeader>
			<CardContent>
				<ol className="relative space-y-3 border-l border-border pl-6">
					{steps.map((step, idx) => {
						// Once the parent operation has terminated, any
						// step still in "running" is a leftover from the
						// pre-finish race and shouldn't keep pulsing. We
						// coerce it to "done" visually so the timeline
						// matches the final operation status.
						const effective =
							opTerminal && step.status === "running" ? "done" : step.status;
						return (
							<StepRow
								key={`${step.step}-${idx}`}
								step={{ ...step, status: effective }}
							/>
						);
					})}
				</ol>
			</CardContent>
		</Card>
	);
}

function StepRow({ step }: { step: TimelineStep }) {
	const duration = computeDuration(step);
	return (
		<li className="relative">
			<span
				className={cn(
					"absolute left-[-33px] flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-background",
					iconBg(step.status),
				)}
				aria-hidden
			>
				<StatusIcon status={step.status} />
			</span>
			<div className="flex flex-col gap-0.5">
				<div className="flex items-baseline gap-2">
					<span
						className={cn(
							"text-sm font-medium",
							step.status === "skipped" && "text-muted-foreground line-through",
							step.status === "pending" && "text-muted-foreground",
						)}
					>
						{step.label}
					</span>
					{duration && (
						<span className="text-xs text-muted-foreground tabular-nums">
							{duration}
						</span>
					)}
				</div>
				{step.status === "running" && (
					// `note` takes precedence over the generic placeholder
					// so an operator stuck behind another publish sees the
					// queue context instead of a meaningless "In progress…".
					<span className="text-xs italic text-muted-foreground">
						{step.note ?? "In progress…"}
					</span>
				)}
				{step.status === "failed" && step.error && (
					<span className="text-xs text-destructive break-all">
						{step.error}
					</span>
				)}
			</div>
		</li>
	);
}

function StatusIcon({ status }: { status: StepStatus }) {
	switch (status) {
		case "running":
			return <Loader2 className="h-3.5 w-3.5 animate-spin text-white" />;
		case "done":
			return <Check className="h-3.5 w-3.5 text-white" />;
		case "failed":
			return <X className="h-3.5 w-3.5 text-white" />;
		case "skipped":
			return <Minus className="h-3.5 w-3.5 text-white" />;
		case "pending":
		default:
			// Empty circle for pending steps — the ring + background
			// already convey "not started".
			return null;
	}
}

function iconBg(status: StepStatus): string {
	switch (status) {
		case "running":
			return "bg-blue-500 animate-pulse";
		case "done":
			return "bg-emerald-500";
		case "failed":
			return "bg-destructive";
		case "skipped":
			return "bg-zinc-400 dark:bg-zinc-600";
		case "pending":
		default:
			return "bg-zinc-300 dark:bg-zinc-700";
	}
}

/**
 * Human-friendly duration for the step. Falls back to "—" while the
 * step has no startedAt (pending) and to a live "1.2s" counter for
 * running steps so the user gets feedback even before the step
 * completes.
 */
function computeDuration(step: TimelineStep): string | null {
	if (!step.startedAt) return null;
	const start = Date.parse(step.startedAt);
	const end = step.finishedAt ? Date.parse(step.finishedAt) : Date.now();
	if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
	const ms = Math.max(0, end - start);
	if (ms < 1000) return `${ms}ms`;
	if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
	const mins = Math.floor(ms / 60_000);
	const secs = Math.floor((ms % 60_000) / 1000);
	return `${mins}m${secs.toString().padStart(2, "0")}s`;
}
